import { FetchError } from "../exceptions";

export const API_URL = process.env.REACT_APP_BACKEND_URL;
export const PUBLIC_BASE_URL = "";

const cache: { [key: string]: any } = {};

export const fetchPublic = (path: string): Promise<any> => {
  const url = `${PUBLIC_BASE_URL}${path}`;

  // Check if the data for this URL is already in cache
  if (cache[url]) {
    // Return the cached data as a promise
    return Promise.resolve(cache[url]);
  }

  // If not in cache, fetch it and then cache the result
  return fetch(url)
    .then((res) => res.json())
    .then((data) => {
      cache[url] = data; // Cache the fetched data
      return data;
    });
};

export const fetchSound = (path: string) => {
  return fetch(`${PUBLIC_BASE_URL}${path}`).then((res) => res.url);
};

/** Longest error text worth showing — beyond this it is a page, not a message. */
const MAX_ERROR_LENGTH = 300;

/**
 * Unwraps a Python exception repr into its message. The backend's `except
 * TypeError` branches respond with `error.__repr__()`, which reaches us as
 * e.g. `TypeError("AddressIM.__init__() missing 1 required positional
 * argument: 'phone'")`.
 *
 * Only exception-shaped names are unwrapped, and the quoted body may not
 * itself contain the quote character, so ordinary prose that happens to end in
 * a parenthesis — `Order("A-1") cannot be linked to customer ("B-2")` — is
 * returned untouched instead of being mangled.
 */
const unwrapPythonRepr = (raw: string): string => {
  const match = raw
    .trim()
    .match(
      /^[A-Za-z_][A-Za-z0-9_.]*(?:Error|Exception|Warning)\((['"])((?:(?!\1)[\s\S])*)\1,?\)$/
    );
  return match ? match[2] : raw;
};

/** True for a body that is an HTML page (proxy 502, Flask debug page, …). */
const isHtml = (raw: string): boolean =>
  /^<(?:!doctype|html|head|body|\?xml)\b/i.test(raw);

/**
 * Flattens whatever a JSON error field holds into readable text. Values are
 * not always strings — a structured body such as
 * `{"detail": [{"loc": ["body", "phone"], "msg": "field required"}]}` would
 * otherwise be shown to the user as "[object Object]".
 */
const flattenMessage = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value))
    return value.map(flattenMessage).filter(Boolean).join("\n");
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    // Prefer a nested message field, else render the entries as "field: msg".
    const nested = record.msg ?? record.message ?? record.detail;
    if (nested != null) return flattenMessage(nested);
    return Object.entries(record)
      .map(([key, val]) => {
        const text = flattenMessage(val);
        return text ? `${key}: ${text}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return "";
};

/**
 * Reads an error response body and pulls out the most useful message the
 * backend gave us.
 *
 * The Flask backend is inconsistent: most handlers respond with a plain-text
 * body (`make_response(error.message, status)`), while a few — e.g. POST
 * /users — respond with `{"message": ...}`. Both are handled here, along with
 * a Python exception repr leaking through the `TypeError` branches.
 *
 * Falls back to the status line whenever the body is missing, an HTML error
 * page, or too long to be a message, so the user never sees an empty string
 * (statusText is always empty over HTTP/2) nor a page-filling dump.
 */
export const parseErrorResponse = async (res: Response): Promise<string> => {
  const fallback = res.statusText
    ? `${res.statusText} (${res.status})`
    : `Request failed (${res.status})`;

  let raw = "";
  try {
    raw = await res.text();
  } catch {
    return fallback;
  }
  raw = raw.trim();
  if (!raw || isHtml(raw)) return fallback;

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    // Plain-text body — the common case for this backend.
    data = raw;
  }

  const message =
    typeof data === "string"
      ? unwrapPythonRepr(data)
      : unwrapPythonRepr(
          flattenMessage(
            (data as any)?.message ??
              (data as any)?.detail ??
              (data as any)?.error ??
              (data as any)?.errors
          )
        );

  if (!message) return fallback;
  return message.length > MAX_ERROR_LENGTH ? fallback : message;
};

/** Throws a FetchError carrying the backend's own error message. */
export const throwFetchError = (res: Response): Promise<never> =>
  parseErrorResponse(res).then((message) => {
    throw new FetchError(message, res.status);
  });
